// CanalesResumenSection — resumen consolidado Limpack + ML para el Dashboard admin.
// 3 cards: canal Limpack (MRR + ventas) · canal ML (ventas + operativos) · consolidado (bruto + neto + split).
// Datos: kits + clientes activos + pedidos 30d + ml_ventas 30d.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const fmt = (n) => '$ ' + Math.round(Number(n || 0)).toLocaleString('es-UY')

export default function CanalesResumenSection() {
  const [data, setData] = useState(null)

  useEffect(() => {
    async function cargar() {
      const now = Date.now()
      const hace30 = new Date(now - 30 * 86400000).toISOString()
      const hace2h = new Date(now - 2 * 3600000).toISOString()

      const [kitsRes, clientesRes, pedidosB2CRes, pedidosB2BRes, pagosSusc30Res, mlVentas30Res, mlPubsRes, mlUrgentesRes] = await Promise.all([
        supabase.from('kits').select('codigo, precio_susc').eq('activo', true),
        // Solo datos reales (excluye es_prueba)
        supabase.from('clientes').select('id, estado, kit_actual').eq('es_prueba', false),
        // B2C (personas físicas): pedidos con pagado_at seteado en últimos 30d
        supabase.from('pedidos').select('id, monto_total, pagado_at').eq('es_prueba', false).is('empresa_id', null).not('estado', 'in', '(cancelado,rechazado)').gte('pagado_at', hace30),
        // B2B (empresas): pedidos entregados/en_ruta/pagado en últimos 30d.
        // Los B2B se cobran vía facturas_emitidas.cobro_b2b_estado, no pagado_at.
        supabase.from('pedidos').select('id, monto_total, empresa_id, estado').eq('es_prueba', false).not('empresa_id', 'is', null).in('estado', ['entregado', 'en_ruta', 'pagado']).gte('created_at', hace30),
        // Cobros suscripción (van a pagos, no a pedidos)
        supabase.from('pagos').select('id, monto').eq('estado', 'pagado').eq('es_prueba', false).gte('pagado_at', hace30),
        // BUG fix 2026-09-02: excluir canceladas. "Ventas" no puede contar ventas canceladas.
        // Antes sumaba 3 canceladas ($1.646) e inflaba bruto (50.815->49.169) y pedidos (66->63).
        supabase.from('ml_ventas').select('monto_bruto, comision_monto, costo_fijo, neto, neto_real').neq('status', 'cancelled').gte('date_created', hace30),
        supabase.from('ml_publicaciones').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('ml_preguntas').select('id', { count: 'exact', head: true }).eq('status', 'unanswered').lt('date_created', hace2h),
      ])

      const kitPrecio = Object.fromEntries((kitsRes.data || []).map(k => [k.codigo, Number(k.precio_susc || 0)]))
      const activos = (clientesRes.data || []).filter(c => c.estado === 'activa')
      const mrr = activos.reduce((s, c) => s + (kitPrecio[c.kit_actual] || 0), 0)

      const pedidosB2C = pedidosB2CRes.data || []
      const pedidosB2B = pedidosB2BRes.data || []
      const pagosSusc30 = pagosSusc30Res.data || []
      const lpBrutoB2C  = pedidosB2C.reduce((s, p) => s + Number(p.monto_total || 0), 0)
      const lpBrutoB2B  = pedidosB2B.reduce((s, p) => s + Number(p.monto_total || 0), 0)
      const lpBrutoSusc = pagosSusc30.reduce((s, p) => s + Number(p.monto || 0), 0)
      const lpBrutoTienda = lpBrutoB2C + lpBrutoB2B
      const lpBruto30 = lpBrutoTienda + lpBrutoSusc
      const lpPedidos30Tot = pedidosB2C.length + pedidosB2B.length + pagosSusc30.length

      const mlVentas30 = mlVentas30Res.data || []
      const mlBruto30 = mlVentas30.reduce((s, v) => s + Number(v.monto_bruto || 0), 0)
      const mlNeto30 = mlVentas30.reduce((s, v) => s + Number(v.neto || 0), 0)            // neto producto (bruto - comision - costo fijo)
      const mlNetoCaja30 = mlVentas30.reduce((s, v) => s + Number(v.neto_real || 0), 0)   // neto en caja (+ flete que cobramos / compensacion)
      const mlComision30 = mlVentas30.reduce((s, v) => s + Number(v.comision_monto || 0) + Number(v.costo_fijo || 0), 0)

      // Aproximación neto Limpack: bruto − ~5% comisión MP (MCC 5411).
      // Desde 3-jul-2026 MP es el procesador único (Handy/Plexo quedaron standby).
      // TODO: leer comisiones reales desde asientos Zeta para precisión contable.
      const lpNeto30Approx = Math.round(lpBruto30 * 0.95)
      const brutoTotal = lpBruto30 + mlBruto30
      const netoTotal = lpNeto30Approx + mlNeto30           // neto producto consolidado
      const netoCajaTotal = lpNeto30Approx + mlNetoCaja30   // neto en caja consolidado (con flete ML)

      const pedidosTotal = lpPedidos30Tot + mlVentas30.length
      setData({
        // Limpack
        mrr, suscriptores: activos.length,
        lp_bruto_30d: lpBruto30, lp_pedidos_30d: lpPedidos30Tot,
        // Breakdown Limpack por tipo
        lp_bruto_b2c: lpBrutoB2C, lp_pedidos_b2c: pedidosB2C.length,
        lp_bruto_b2b: lpBrutoB2B, lp_pedidos_b2b: pedidosB2B.length,
        lp_bruto_susc: lpBrutoSusc, lp_cobros_susc: pagosSusc30.length,
        // ML
        ml_bruto_30d: mlBruto30, ml_pedidos_30d: mlVentas30.length,
        ml_publicaciones: mlPubsRes.count || 0,
        ml_preguntas_urgentes: mlUrgentesRes.count || 0,
        ml_comision_30d: mlComision30,
        // Consolidado
        bruto_total: brutoTotal,
        neto_total: netoTotal,
        neto_caja_total: netoCajaTotal,
        pedidos_total: pedidosTotal,
        ticket_promedio: pedidosTotal ? Math.round(brutoTotal / pedidosTotal) : 0,
        pct_limpack: brutoTotal ? (lpBruto30 / brutoTotal * 100) : 0,
        pct_ml: brutoTotal ? (mlBruto30 / brutoTotal * 100) : 0,
      })
    }
    cargar()
    const t = setInterval(cargar, 60000)
    return () => clearInterval(t)
  }, [])

  if (!data) return null

  return (
    <section className="cr-section">
      <div className="cr-head">
        <h2 className="cr-h2">📊 Canales · resumen 30 días</h2>
        <div className="cr-tot">Bruto total: <strong>{fmt(data.bruto_total)}</strong></div>
      </div>

      <div className="cr-grid">
        {/* CARD LIMPACK */}
        <Link to="/admin/canales/limpack" className="cr-card cr-card-lp">
          <div className="cr-card-head">
            <span className="cr-badge cr-badge-lp">🧴 LIMPACK</span>
            <span className="cr-card-hint">Ver hub →</span>
          </div>
          <div className="cr-hero">
            <div className="cr-hero-lbl">Ventas 30 días (bruto)</div>
            <div className="cr-hero-num">{fmt(data.lp_bruto_30d)}</div>
            <div className="cr-hero-hint">MRR recurrente {fmt(data.mrr)}</div>
          </div>
          <div className="cr-metrics">
            <div><span>Suscripciones</span><strong>{fmt(data.lp_bruto_susc)}</strong><em>{data.lp_cobros_susc} cobros</em></div>
            <div><span>B2C one-shot</span><strong>{fmt(data.lp_bruto_b2c)}</strong><em>{data.lp_pedidos_b2c} pedidos</em></div>
            <div><span>B2B empresas</span><strong>{fmt(data.lp_bruto_b2b)}</strong><em>{data.lp_pedidos_b2b} pedidos</em></div>
          </div>
        </Link>

        {/* CARD MERCADOLIBRE */}
        <Link to="/admin/canales/mercadolibre" className="cr-card cr-card-ml">
          <div className="cr-card-head">
            <span className="cr-badge cr-badge-ml">🤝 MERCADOLIBRE</span>
            <span className="cr-card-hint">Ver hub →</span>
          </div>
          <div className="cr-hero">
            <div className="cr-hero-lbl">Ventas 30 días</div>
            <div className="cr-hero-num">{fmt(data.ml_bruto_30d)}</div>
          </div>
          <div className="cr-metrics">
            <div><span>Pedidos 30d</span><strong>{data.ml_pedidos_30d}</strong></div>
            <div><span>Publicaciones</span><strong>{data.ml_publicaciones}</strong></div>
            <div className={data.ml_preguntas_urgentes > 0 ? 'cr-warn' : ''}>
              <span>Preg. urgentes</span><strong>{data.ml_preguntas_urgentes}</strong>
            </div>
          </div>
        </Link>

        {/* CARD CONSOLIDADO */}
        <div className="cr-card cr-card-total">
          <div className="cr-card-head">
            <span className="cr-badge cr-badge-total">📊 CONSOLIDADO</span>
          </div>
          <div className="cr-hero">
            <div className="cr-hero-lbl">Neto producto 30d</div>
            <div className="cr-hero-num cr-num-neto">{fmt(data.neto_total)}</div>
            <div className="cr-hero-hint">En caja con flete: {fmt(data.neto_caja_total)}</div>
          </div>
          {/* Barra de split */}
          <div className="cr-split">
            <div className="cr-split-bar">
              <div className="cr-split-lp" style={{ width: `${data.pct_limpack}%` }} title={`Limpack · ${fmt(data.lp_bruto_30d)}`} />
              <div className="cr-split-ml" style={{ width: `${data.pct_ml}%` }} title={`ML · ${fmt(data.ml_bruto_30d)}`} />
            </div>
            <div className="cr-split-labels">
              <span>🧴 {data.pct_limpack.toFixed(0)}%</span>
              <span>🤝 {data.pct_ml.toFixed(0)}%</span>
            </div>
          </div>
          <div className="cr-metrics">
            <div><span>Pedidos totales</span><strong>{data.pedidos_total}</strong></div>
            <div><span>Ticket promedio</span><strong>{fmt(data.ticket_promedio)}</strong></div>
          </div>
        </div>
      </div>

      <style>{CSS}</style>
    </section>
  )
}

const CSS = `
.cr-section { margin-bottom: 20px; }
.cr-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; padding: 0 4px; }
.cr-h2 { font-size: 15px; color: #1A3461; margin: 0; font-weight: 700; }
.cr-tot { font-size: 12.5px; color: #64748B; }
.cr-tot strong { color: #1A3461; font-weight: 700; }
.cr-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.cr-card { display: block; background: #fff; border: 1px solid #E6E2D8; border-radius: 12px; padding: 16px 18px; text-decoration: none; color: inherit; transition: transform .12s, box-shadow .12s; position: relative; overflow: hidden; }
.cr-card:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(26, 52, 97, .08); }
.cr-card-lp { border-top: 3px solid #00A8B5; }
.cr-card-ml { border-top: 3px solid #FFE566; }
.cr-card-total { border-top: 3px solid #1A3461; }
.cr-card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.cr-badge { font-size: 10.5px; font-weight: 800; letter-spacing: 0.7px; text-transform: uppercase; padding: 3px 9px; border-radius: 6px; }
.cr-badge-lp { background: #E1F5EE; color: #085041; }
.cr-badge-ml { background: #FFF4C7; color: #92610A; }
.cr-badge-total { background: #DBEAFE; color: #1E3A8A; }
.cr-card-hint { font-size: 11px; color: #94A3B8; }
.cr-hero { margin-bottom: 12px; }
.cr-hero-lbl { font-size: 10.5px; color: #94A3B8; letter-spacing: 0.6px; text-transform: uppercase; font-weight: 600; }
.cr-hero-num { font-size: 30px; color: #1A3461; font-weight: 800; line-height: 1.1; margin-top: 2px; }
.cr-num-neto { color: #10B981; }
.cr-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; padding-top: 10px; border-top: 1px solid #F1EFE8; }
.cr-metrics > div { font-size: 11.5px; }
.cr-metrics span { display: block; color: #94A3B8; font-size: 10px; letter-spacing: 0.4px; text-transform: uppercase; font-weight: 600; margin-bottom: 2px; }
.cr-metrics strong { color: #1A3461; font-weight: 700; font-size: 13px; display: block; }
.cr-metrics em { display: block; color: #94A3B8; font-size: 10px; font-style: normal; font-weight: 500; margin-top: 1px; }
.cr-metrics .cr-warn strong { color: #DC2626; }
.cr-hero-hint { font-size: 10.5px; color: #94A3B8; margin-top: 3px; font-weight: 500; }
.cr-split { margin-bottom: 12px; }
.cr-split-bar { display: flex; height: 8px; border-radius: 999px; overflow: hidden; background: #F1F5F9; }
.cr-split-lp { background: #00A8B5; }
.cr-split-ml { background: #FFE566; }
.cr-split-labels { display: flex; justify-content: space-between; font-size: 10.5px; color: #64748B; margin-top: 4px; font-weight: 600; }

@media (max-width: 1024px) {
  .cr-grid { grid-template-columns: 1fr; }
}
`
